import { createTheme, Theme } from '@mui/material/styles';
import { SLATE } from './chrome';

// Orange theme colors
const orangeColors = {
  primary: {
    main: '#ff8c00', // Orange for primary actions
    light: '#ffb74d',
    dark: '#f57c00',
    contrastText: '#000000', // Black text on orange buttons
  },
  secondary: {
    main: '#1976d2', // Blue for secondary actions
    light: '#42a5f5',
    dark: '#1565c0',
  },
  sidebar: {
    dark: '#1a2233', // Dark blue for sidebar
    light: '#ffffff',
  },
  background: {
    default: '#f8f9fb', // Light gray for main content
    paper: '#ffffff',
    sidebar: '#1a2233',
  },
  text: {
    primary: '#2c3e50',
    secondary: '#6c757d',
    sidebar: '#ffffff',
  },
  success: {
    main: '#28a745',
    light: '#81c784',
    dark: '#388e3c',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#ffc107',
    light: '#ffcd3c',
    dark: '#ff8f00',
    contrastText: '#000000',
  },
  error: {
    main: '#dc3545',
    light: '#e57373',
    dark: '#d32f2f',
    contrastText: '#ffffff',
  },
  info: {
    main: '#17a2b8',
    light: '#64b5f6',
    dark: '#1976d2',
    contrastText: '#ffffff',
  },
};

// Light Theme
export const lightTheme: Theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2', // Blue for primary actions in light theme
      light: '#42a5f5',
      dark: '#1565c0',
      contrastText: '#ffffff',
    },
    secondary: orangeColors.secondary,
    background: {
      default: orangeColors.background.default,
      paper: orangeColors.background.paper,
    },
    text: {
      primary: orangeColors.text.primary,
      secondary: orangeColors.text.secondary,
    },
    success: orangeColors.success,
    warning: orangeColors.warning,
    error: orangeColors.error,
    info: orangeColors.info,
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: orangeColors.background.paper,
          color: orangeColors.text.primary,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
        },
        contained: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
        outlined: {
          backgroundColor: '#ffffff',
          '&:hover': {
            backgroundColor: '#f5f5f5',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#ffffff',
            borderRadius: 8,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          backgroundColor: '#ffffff',
          borderRadius: 8,
        },
      },
    },
  },
});

// Dark Theme
export const darkTheme: Theme = createTheme({
  palette: {
    mode: 'dark',
    primary: orangeColors.primary,
    secondary: orangeColors.secondary,
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b3b3b3',
    },
    success: orangeColors.success,
    warning: orangeColors.warning,
    error: orangeColors.error,
    info: orangeColors.info,
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#121212',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e1e1e',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
        },
        containedPrimary: {
          backgroundColor: orangeColors.primary.main,
          '&:hover': {
            backgroundColor: orangeColors.primary.dark,
          },
        },
        containedSecondary: {
          backgroundColor: orangeColors.secondary.main,
          color: '#ffffff',
          '&:hover': {
            backgroundColor: orangeColors.secondary.dark,
          },
        },
        outlined: {
          backgroundColor: '#ffffff',
          color: orangeColors.primary.main,
          borderColor: orangeColors.primary.main,
          '&:hover': {
            backgroundColor: '#f5f5f5',
            borderColor: orangeColors.primary.dark,
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: '0 20px 20px 0',
          marginRight: 16,
          '&.Mui-selected': {
            backgroundColor: orangeColors.secondary.main,
            color: '#ffffff',
            '&:hover': {
              backgroundColor: orangeColors.secondary.dark,
            },
            '& .MuiListItemIcon-root': {
              color: '#ffffff',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(255, 140, 0, 0.08)',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: orangeColors.background.sidebar,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e1e1e',
          border: '1px solid #333',
          borderRadius: 12,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#ffffff', // Changed to white for better contrast with weather background
            '& fieldset': {
              borderColor: '#333',
            },
            '&:hover fieldset': {
              borderColor: orangeColors.secondary.main,
            },
            '&.Mui-focused fieldset': {
              borderColor: orangeColors.secondary.main,
            },
          },
        },
      },
    },
  },
});

// Orange Theme
export const orangeTheme: Theme = createTheme({
  palette: {
    mode: 'light',
    primary: orangeColors.primary,
    secondary: orangeColors.secondary,
    background: {
      default: orangeColors.background.default,
      paper: orangeColors.background.paper,
    },
    text: {
      primary: orangeColors.text.primary,
      secondary: orangeColors.text.secondary,
    },
    success: orangeColors.success,
    warning: orangeColors.warning,
    error: orangeColors.error,
    info: orangeColors.info,
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
      color: orangeColors.text.primary,
    },
    h5: {
      fontWeight: 600,
      color: orangeColors.text.primary,
    },
    h6: {
      fontWeight: 600,
      color: orangeColors.text.primary,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: orangeColors.background.paper,
          color: orangeColors.text.primary,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: orangeColors.background.paper,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
        },
        contained: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
        containedPrimary: {
          backgroundColor: orangeColors.primary.main,
          color: orangeColors.primary.contrastText,
          '&:hover': {
            backgroundColor: orangeColors.primary.dark,
          },
        },
        containedSecondary: {
          backgroundColor: orangeColors.secondary.main,
          color: '#ffffff',
          '&:hover': {
            backgroundColor: orangeColors.secondary.dark,
          },
        },
        outlined: {
          backgroundColor: '#ffffff',
          '&:hover': {
            backgroundColor: '#f5f5f5',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: '0 20px 20px 0',
          marginRight: 16,
          '&.Mui-selected': {
            backgroundColor: orangeColors.secondary.main,
            color: '#ffffff',
            '&:hover': {
              backgroundColor: orangeColors.secondary.dark,
            },
            '& .MuiListItemIcon-root': {
              color: '#ffffff',
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: orangeColors.background.paper,
          border: '1px solid #e0e0e0',
          borderRadius: 12,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#ffffff',
            borderRadius: 8,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          backgroundColor: '#ffffff',
          borderRadius: 8,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          // Hover styles moved to globalStyles.css for gradient support
          fontWeight: 600,
        },
      },
    },
  },
});

// Slate surfaces read as hairline-bordered rather than shadowed: every
// elevation draws a 1px ring (so a default <Paper>/<Card> gets the border
// with no layout shift), and only floating layers (menus, dialogs — elevation
// 5+) add a soft drop shadow on top.
const SLATE_RING = `0 0 0 1px ${SLATE.border}`;
const slateShadows = [
  'none',
  ...Array.from({ length: 4 }, () => SLATE_RING),
  ...Array.from({ length: 20 }, (_, i) =>
    `${SLATE_RING}, 0 ${4 + i}px ${12 + 2 * i}px rgba(15, 42, 68, 0.12)`),
] as Theme['shadows'];

// Slate Theme — "enterprise workbench": navy chrome, white content, hairline
// borders instead of drop shadows, underlined tabs, soft tinted chips.
// Chrome (AppBar / sidebar) colours live in ./chrome.ts.
export const slateTheme: Theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: SLATE.accent,
      light: '#3f83f8',
      dark: '#1e429f',
      contrastText: '#ffffff',
    },
    secondary: {
      main: SLATE.navy,
      light: SLATE.navyRaised,
      dark: '#0a1d30',
      contrastText: '#ffffff',
    },
    background: {
      default: SLATE.surface,
      paper: SLATE.surface,
    },
    text: {
      primary: SLATE.text,
      secondary: SLATE.textMuted,
    },
    divider: SLATE.border,
    success: { main: '#047857', light: '#10b981', dark: '#065f46', contrastText: '#ffffff' },
    warning: { main: '#b45309', light: '#f59e0b', dark: '#92400e', contrastText: '#ffffff' },
    error: { main: '#c81e1e', light: '#f05252', dark: '#9b1c1c', contrastText: '#ffffff' },
    info: { main: SLATE.accent, light: '#3f83f8', dark: '#1e429f', contrastText: '#ffffff' },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h3: { fontWeight: 700, letterSpacing: '-0.02em' },
    h4: { fontWeight: 700, letterSpacing: '-0.015em' },
    h5: { fontWeight: 600, letterSpacing: '-0.01em' },
    h6: { fontWeight: 600 },
    overline: { fontWeight: 600, letterSpacing: '0.08em' },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: {
    borderRadius: 6,
  },
  shadows: slateShadows,
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: SLATE.navy,
          color: '#ffffff',
          boxShadow: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        outlined: {
          borderColor: SLATE.border,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          border: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 6,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 44,
          borderBottom: `1px solid ${SLATE.border}`,
        },
        indicator: {
          height: 2,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          minHeight: 44,
          color: '#374151',
          '&.Mui-selected': {
            fontWeight: 600,
          },
        },
      },
    },
    // Soft chips: tinted fill, dark text — "High risk" / "Medium" style.
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 600,
          // [color, tint, hover tint, text]. The hover tint replaces MUI's
          // clickable `palette[color].dark` hover, which would equal the text.
          variants: ([
            ['error', '#fde8e8', '#fbd5d5', '#9b1c1c'],
            ['warning', '#fef3c7', '#fde68a', '#92400e'],
            ['success', '#d1fae5', '#a7f3d0', '#065f46'],
            ['info', '#e1effe', '#c3ddfd', '#1e429f'],
            ['primary', '#e1effe', '#c3ddfd', '#1e429f'],
          ] as const).map(([color, bg, hoverBg, fg]) => ({
            props: { variant: 'filled', color },
            style: {
              backgroundColor: bg,
              color: fg,
              '& .MuiChip-icon, & .MuiChip-deleteIcon': { color: fg },
              '&.MuiChip-clickable:hover, &.MuiChip-clickable.Mui-focusVisible, &.MuiChip-deletable.Mui-focusVisible': {
                backgroundColor: hoverBg,
              },
            },
          })),
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standard: {
          border: '1px solid transparent',
        },
        standardError: { backgroundColor: '#fdf2f2', borderColor: '#f8b4b4', color: '#9b1c1c' },
        standardWarning: { backgroundColor: '#fffbeb', borderColor: '#fcd34d', color: '#92400e' },
        standardInfo: { backgroundColor: '#ebf5ff', borderColor: '#a4cafe', color: '#1e429f' },
        standardSuccess: { backgroundColor: '#ecfdf5', borderColor: '#84e1bc', color: '#065f46' },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 6,
          borderRadius: 3,
          backgroundColor: SLATE.border,
        },
        bar: {
          borderRadius: 3,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: SLATE.surface,
            borderRadius: 6,
            '& fieldset': { borderColor: '#d1d5db' },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          backgroundColor: SLATE.surface,
          borderRadius: 6,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: SLATE.border,
        },
        head: {
          fontWeight: 600,
          color: SLATE.textMuted,
          backgroundColor: SLATE.surfaceMuted,
        },
      },
    },
  },
});

export const getTheme = (mode: string): Theme => {
  switch (mode) {
    case 'dark':
      return darkTheme;
    case 'orange':
      return orangeTheme;
    case 'slate':
      return slateTheme;
    default:
      return lightTheme;
  }
};
